import axios from 'axios';
import api from './api';

/** Parti S3 in parallelo: basso per non saturare la banda dell'utente. */
const CHUNK_CONCURRENCY = 2;
const PART_MAX_RETRIES = 4;
const PART_RETRY_BASE_MS = 800;

export const formatFileSize = (size) => {
  if (size == null) return '-';
  if (size < 1024) return `${size} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let value = size / 1024;
  let unit = units[0];
  for (let i = 1; i < units.length && value >= 1024; i += 1) {
    value /= 1024;
    unit = units[i];
  }
  return `${value.toFixed(value >= 10 ? 1 : 2)} ${unit}`;
};

export const listDocumentiAppuntamento = async (idAppuntamento) => {
  const res = await api.get(`/appuntamenti/${idAppuntamento}/documenti`);
  return Array.isArray(res.data) ? res.data : [];
};

const getDocumentoUrl = async (idDocumento, disposition) => {
  const res = await api.get(`/documenti-paziente/${idDocumento}/download-url`, {
    params: { disposition },
  });
  return {
    url: res.data?.url,
    filename: res.data?.filename || 'documento',
  };
};

/** Apre il file nel browser (Content-Disposition: inline). */
export const viewDocumentoPaziente = async (idDocumento) => {
  const { url } = await getDocumentoUrl(idDocumento, 'inline');
  if (url) {
    window.open(url, '_blank', 'noopener,noreferrer');
  }
};

/**
 * Forza il download (Content-Disposition: attachment).
 * Legacy alias: downloadDocumentoPaziente.
 */
export const downloadDocumentoPaziente = async (idDocumento) => {
  const { url, filename } = await getDocumentoUrl(idDocumento, 'attachment');
  if (!url) return;
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.rel = 'noopener';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
};

/** Soft limit: zip nel browser ok. Sopra: chiedi conferma. */
export const ZIP_SOFT_LIMIT_BYTES = 200 * 1024 * 1024;
/** Hard: zip client rischioso per memoria; meglio scaricare singoli. */
export const ZIP_HARD_LIMIT_BYTES = 1024 * 1024 * 1024;

export const sumDocumentSizes = (docs = []) =>
  docs.reduce((sum, doc) => sum + (Number(doc.dimensioneFile) || 0), 0);

/**
 * Scarica tutti i documenti come ZIP creato nel browser (fetch da S3, zero carico ECS).
 * @param {Array<{id:number,filename?:string,dimensioneFile?:number}>} docs
 * @param {{ zipName?: string, onProgress?: (pct:number)=>void }} options
 */
export const downloadDocumentiAsZip = async (docs, options = {}) => {
  const list = Array.isArray(docs) ? docs.filter((d) => d?.id != null) : [];
  if (list.length === 0) {
    throw new Error('Nessun documento da scaricare');
  }
  const total = sumDocumentSizes(list);
  if (total > ZIP_HARD_LIMIT_BYTES) {
    throw new Error(
      'I file superano 1 GB in totale: scaricali uno a uno (lo zip nel browser rischia di esaurire la memoria).'
    );
  }
  const JSZip = (await import('jszip')).default;
  const zip = new JSZip();
  const usedNames = new Set();
  let done = 0;

  for (const doc of list) {
    const { url, filename } = await getDocumentoUrl(doc.id, 'attachment');
    if (!url) {
      throw new Error(`URL non disponibile per ${doc.filename || doc.id}`);
    }
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Download fallito per ${filename}`);
    }
    const blob = await response.blob();
    let name = (doc.filename || filename || `documento-${doc.id}`).replace(/[\\/]+/g, '_');
    if (usedNames.has(name)) {
      const dot = name.lastIndexOf('.');
      name = dot > 0
        ? `${name.slice(0, dot)}_${doc.id}${name.slice(dot)}`
        : `${name}_${doc.id}`;
    }
    usedNames.add(name);
    zip.file(name, blob);
    done += 1;
    if (typeof options.onProgress === 'function') {
      options.onProgress(Math.round((done / list.length) * 90));
    }
  }

  const zipBlob = await zip.generateAsync(
    { type: 'blob', compression: 'DEFLATE', compressionOptions: { level: 5 } },
    (meta) => {
      if (typeof options.onProgress === 'function' && meta?.percent != null) {
        options.onProgress(90 + Math.round(meta.percent * 0.1));
      }
    }
  );
  const zipName = options.zipName || `documenti-${new Date().toISOString().slice(0, 10)}.zip`;
  const objectUrl = URL.createObjectURL(zipBlob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = zipName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(objectUrl), 30_000);
  if (typeof options.onProgress === 'function') options.onProgress(100);
};

export const deleteDocumentoPaziente = async (idDocumento) => {
  await api.delete(`/documenti-paziente/${idDocumento}`);
};

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const extractEtag = (headers) => {
  const raw = headers?.etag || headers?.ETag || headers?.['etag'];
  if (!raw) return null;
  const value = String(raw).trim();
  // CompleteMultipartUpload si aspetta l'ETag come restituito da S3 (di solito con virgolette).
  return value.startsWith('"') ? value : `"${value}"`;
};

/**
 * PUT di una parte su S3 con retry (rete intermittente / 5xx).
 * Nessun Content-Type custom: riduce attrito CORS e non entra nella firma UploadPart.
 */
const putPartWithRetry = async (url, blob, onPartProgress) => {
  let lastError;
  for (let attempt = 0; attempt < PART_MAX_RETRIES; attempt += 1) {
    try {
      const response = await axios.put(url, blob, {
        // Non usare api instance: niente Authorization verso S3.
        headers: {},
        maxRedirects: 0,
        onUploadProgress: (event) => {
          if (typeof onPartProgress === 'function') {
            onPartProgress(event.loaded || 0);
          }
        },
        // Evita che axios serializzi il blob
        transformRequest: [(data) => data],
      });
      const eTag = extractEtag(response.headers);
      if (!eTag) {
        throw new Error('ETag S3 mancante (verifica CORS ExposeHeaders: ETag)');
      }
      return eTag;
    } catch (error) {
      lastError = error;
      const status = error.response?.status;
      const retryable = !status || status === 408 || status === 429 || status >= 500;
      if (!retryable || attempt === PART_MAX_RETRIES - 1) {
        throw error;
      }
      await sleep(PART_RETRY_BASE_MS * 2 ** attempt);
    }
  }
  throw lastError;
};

const uploadSingle = async (idAppuntamento, file, uploadInfo, onProgress) => {
  await axios.put(uploadInfo.url, file, {
    headers: { 'Content-Type': file.type || 'application/octet-stream' },
    transformRequest: [(data) => data],
    onUploadProgress: (event) => {
      if (event.total) onProgress(Math.round((event.loaded / event.total) * 100));
    },
  });
  const finalize = await api.post(`/appuntamenti/${idAppuntamento}/documenti/finalize`, {
    key: uploadInfo.key,
    filename: file.name,
    contentType: file.type || 'application/octet-stream',
    size: file.size,
    descrizione: uploadInfo.descrizione,
  });
  return finalize.data;
};

/**
 * Multipart diretto browser → S3 (presigned parts).
 * Il backend riceve solo metadata/finalize: non satura l'istanza ECS.
 */
const uploadMultipart = async (idAppuntamento, file, uploadInfo, onProgress) => {
  const partSize = Number(uploadInfo.partSize);
  const parts = uploadInfo.parts || [];
  if (!partSize || parts.length === 0) {
    throw new Error('Piano multipart incompleto');
  }

  const completedParts = [];
  const loadedByPart = {};
  let nextIndex = 0;

  const reportProgress = () => {
    const loaded = Object.values(loadedByPart).reduce((sum, value) => sum + value, 0);
    onProgress(Math.min(99, Math.round((loaded / file.size) * 100)));
  };

  const worker = async () => {
    while (nextIndex < parts.length) {
      const current = parts[nextIndex];
      nextIndex += 1;
      const start = (current.partNumber - 1) * partSize;
      const end = Math.min(start + partSize, file.size);
      const blob = file.slice(start, end);
      const eTag = await putPartWithRetry(current.url, blob, (loaded) => {
        loadedByPart[current.partNumber] = loaded;
        reportProgress();
      });
      loadedByPart[current.partNumber] = end - start;
      reportProgress();
      completedParts.push({
        partNumber: current.partNumber,
        eTag,
      });
    }
  };

  await Promise.all(
    Array.from({ length: Math.min(CHUNK_CONCURRENCY, parts.length) }, () => worker())
  );

  completedParts.sort((a, b) => a.partNumber - b.partNumber);

  const finalize = await api.post(`/appuntamenti/${idAppuntamento}/documenti/finalize`, {
    key: uploadInfo.key,
    uploadId: uploadInfo.uploadId,
    filename: file.name,
    contentType: file.type || 'application/octet-stream',
    size: file.size,
    parts: completedParts,
    descrizione: uploadInfo.descrizione,
  });
  onProgress(100);
  return finalize.data;
};

/**
 * Upload documenti paziente: direct-to-S3 (PUT singolo o multipart).
 * Termine tecnico: "presigned multipart upload" / "direct-to-object-storage".
 */
export const uploadDocumentoPaziente = async (idAppuntamento, file, onProgress = () => {}, options = {}) => {
  const uploadRes = await api.post(`/appuntamenti/${idAppuntamento}/documenti/upload-url`, {
    filename: file.name,
    contentType: file.type || 'application/octet-stream',
    size: file.size,
  });
  const uploadInfo = { ...uploadRes.data, descrizione: options.descrizione };
  if (uploadInfo.multipart) {
    return uploadMultipart(idAppuntamento, file, uploadInfo, onProgress);
  }
  return uploadSingle(idAppuntamento, file, uploadInfo, onProgress);
};
